import { useEffect, useMemo, useState } from "react";

import useService from "../../../../hooks/useService";
import { getModalHandler } from "../../Common/Modals/hooks/useModalSingleton";
import {
  hasAnyCustomMedicalItemValues,
  hasCompleteCustomMedicalItemValues,
  INITIAL_CUSTOM_MEDICAL_ITEM_VALUES,
  normalizeIdValue,
} from "../components/medical-breakdown/helpers/customMedicalItem";
import { normalizeNullableCurrencyInput } from "../helper/caseForms";
import { useSettlementStore } from "../store/settlementStore";

import useCustomMedicalItemOptions from "./useCustomMedicalItemOptions";
import useSettlementPermissions from "./useSettlementPermissions";

export default function useAddCustomMedicalItemModal({
  caseId,
  handleCloseModal,
  onSaveSuccess,
  openModal,
}) {
  const providerSettlementRepository = useService("repositories/provider-settlement-repository");
  const { openFailureModal, openSuccessModal } = getModalHandler();
  const bumpRefreshVersion = useSettlementStore((state) => state.bumpRefreshVersion);
  const { canEditMedicalBreakdown } = useSettlementPermissions();
  const [formValues, setFormValues] = useState(INITIAL_CUSTOM_MEDICAL_ITEM_VALUES);
  const [providerSearch, setProviderSearch] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const {
    providerOptions,
    settlementTypeOptions,
    isLoadingProviders,
  } = useCustomMedicalItemOptions({
    openModal,
    providerSearch,
  });

  useEffect(() => {
    if (!openModal) {
      return;
    }

    setFormValues(INITIAL_CUSTOM_MEDICAL_ITEM_VALUES);
    setProviderSearch("");
  }, [openModal]);

  const isDirty = useMemo(() => hasCompleteCustomMedicalItemValues(formValues), [formValues]);

  const hasChanges = useMemo(() => hasAnyCustomMedicalItemValues(formValues), [formValues]);

  const handleFieldOnChange = (fieldName, value) => {
    setFormValues((prev) => ({
      ...prev,
      [fieldName]: fieldName === "settledAmount" ? normalizeNullableCurrencyInput(value) : value,
    }));
  };

  const handleProviderSearch = (value) => {
    setProviderSearch(value);
  };

  const handleCancel = () => {
    setFormValues(INITIAL_CUSTOM_MEDICAL_ITEM_VALUES);
    setProviderSearch("");
  };

  const handleSave = async () => {
    if (!caseId || !isDirty || !canEditMedicalBreakdown) {
      return;
    }

    setIsSaving(true);

    try {
      await providerSettlementRepository.post({
        provider: normalizeIdValue(formValues.providerId),
        case: Number(caseId),
        settled_amount: Number(formValues.settledAmount),
        settlement_type: normalizeIdValue(formValues.settlementType),
      });

      openSuccessModal();
      bumpRefreshVersion();
      handleCloseModal();
      onSaveSuccess?.();
    } catch (error) {
      openFailureModal(error);
    } finally {
      setIsSaving(false);
    }
  };

  return {
    formValues,
    providerOptions,
    settlementTypeOptions,
    isDirty,
    hasChanges,
    isLoadingProviders,
    isSaving,
    handleFieldOnChange,
    handleProviderSearch,
    handleCancel,
    handleSave,
  };
}
